<template>
  <Head>
    <link rel="icon" type="image/x-icon" :href="favicon" />
  </Head>

  <div class="root min-h-screen bg-neutral-50">
    <Toast />
    <ConfirmDialog />

    <!-- Top bar -->
    <header
      v-if="user && !isLoginPage"
      class="sticky top-0 z-50 border-b border-neutral-200 bg-white"
    >
      <div
        class="mx-auto flex h-14 max-w-[1200px] items-center justify-between px-6"
      >
        <!-- Left side: Logo + company name -->
        <div class="flex items-center">
          <Link :href="ordersPath" class="flex items-center no-underline">
            <img class="h-7 w-auto" :src="trellisLogo" alt="Trellis" />
          </Link>
          <div class="mx-4 h-6 w-px bg-neutral-200" />
          <div
            v-if="availableCompanies.length > 1"
            ref="companySwitcherRef"
            class="relative"
          >
            <button
              class="flex cursor-pointer items-center gap-1.5 rounded-lg px-2 py-1 text-[13px] font-medium text-neutral-500 transition-colors hover:bg-neutral-100 hover:text-neutral-700"
              @click="toggleCompanySwitcher"
            >
              {{ selectedCompanyName }}
              <span class="text-[10px] text-neutral-400">&#9662;</span>
            </button>

            <Transition
              enter-active-class="transition ease-out duration-100"
              enter-from-class="transform opacity-0 scale-95"
              enter-to-class="transform opacity-100 scale-100"
              leave-active-class="transition ease-in duration-75"
              leave-from-class="transform opacity-100 scale-100"
              leave-to-class="transform opacity-0 scale-95"
            >
              <div
                v-if="showCompanySwitcher"
                class="absolute left-0 mt-2 w-[260px] origin-top-left rounded-[10px] bg-white py-1 shadow-[0_8px_30px_rgba(0,0,0,0.1)]"
              >
                <div class="border-b border-neutral-100 px-4 py-2.5">
                  <p class="text-navy-800 text-[13px] font-semibold">
                    Switch Company
                  </p>
                </div>
                <div class="max-h-64 overflow-y-auto py-1">
                  <button
                    v-for="company in availableCompanies"
                    :key="company.id"
                    class="flex w-full cursor-pointer items-center gap-3 border-none bg-transparent px-4 py-2.5 text-left transition-colors"
                    :class="
                      company.id === selectedCompanyId
                        ? 'bg-green-50 text-green-700'
                        : 'text-navy-700 hover:bg-neutral-50'
                    "
                    @click="selectCompany(company.id)"
                  >
                    <div
                      class="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[10px] font-semibold"
                      :class="
                        company.id === selectedCompanyId
                          ? 'bg-green-100 text-green-700'
                          : 'bg-navy-100 text-navy-600'
                      "
                    >
                      {{ getCompanyInitials(company.name) }}
                    </div>
                    <span
                      class="min-w-0 flex-1 truncate text-[13px] font-medium"
                    >
                      {{ company.name }}
                    </span>
                    <i
                      v-if="company.id === selectedCompanyId"
                      class="pi pi-check text-xs text-green-600"
                    />
                  </button>
                </div>
              </div>
            </Transition>
          </div>
          <span v-else class="text-[13px] font-medium text-neutral-500">
            {{ selectedCompanyName }}
          </span>
        </div>

        <!-- Right side: Agent health + profile -->
        <div class="flex items-center">
          <!-- Agent health indicator -->
          <div
            v-if="activeAgents.length > 0"
            ref="agentDropdownRef"
            class="relative"
          >
            <button
              class="flex cursor-pointer items-center gap-2 rounded-lg px-2.5 py-1.5 text-[13px] font-medium text-neutral-600 transition-colors hover:bg-neutral-100"
              @click="toggleAgentDropdown"
            >
              <span
                class="inline-block h-[7px] w-[7px] rounded-full"
                :class="allAgentsConnected ? 'bg-green-600' : 'bg-amber-500'"
              />
              {{ activeAgents.length }}
              {{ activeAgents.length === 1 ? "agent" : "agents" }}
            </button>

            <!-- Agent dropdown -->
            <Transition
              enter-active-class="transition ease-out duration-100"
              enter-from-class="transform opacity-0 scale-95"
              enter-to-class="transform opacity-100 scale-100"
              leave-active-class="transition ease-in duration-75"
              leave-from-class="transform opacity-100 scale-100"
              leave-to-class="transform opacity-0 scale-95"
            >
              <div
                v-if="showAgentDropdown"
                class="absolute right-0 mt-2 w-[300px] origin-top-right rounded-[10px] bg-white py-1 shadow-[0_8px_30px_rgba(0,0,0,0.1)]"
              >
                <div class="border-b border-neutral-100 px-4 py-2.5">
                  <p class="text-navy-800 text-[13px] font-semibold">Agents</p>
                </div>

                <div class="max-h-64 overflow-y-auto py-1">
                  <div
                    v-for="agent in activeAgents"
                    :key="agent.id"
                    class="flex items-center justify-between px-4 py-2.5"
                  >
                    <div class="flex items-center gap-2.5">
                      <span
                        class="inline-block h-[7px] w-[7px] rounded-full"
                        :class="
                          agent.status === 'connected'
                            ? 'bg-green-600'
                            : 'bg-amber-500'
                        "
                      />
                      <div>
                        <p class="text-navy-800 text-[13px] font-medium">
                          {{ agent.name || "Unnamed Agent" }}
                        </p>
                        <p class="text-[11px] text-neutral-400">
                          {{ formatHeartbeat(agent.lastHeartbeatAt) }}
                        </p>
                      </div>
                    </div>
                    <span
                      class="rounded-full px-2 py-0.5 text-[11px] font-semibold capitalize"
                      :class="
                        agent.status === 'connected'
                          ? 'bg-green-50 text-green-600'
                          : 'bg-amber-50 text-amber-600'
                      "
                    >
                      {{ agent.status }}
                    </span>
                  </div>
                </div>

                <div class="border-t border-neutral-100 px-4 py-2.5">
                  <Link
                    :href="`/companies/${selectedCompanyId}`"
                    class="text-[13px] font-medium text-green-600 no-underline hover:text-green-700"
                    @click="showAgentDropdown = false"
                  >
                    Manage agents &rarr;
                  </Link>
                </div>
              </div>
            </Transition>
          </div>

          <div
            v-if="activeAgents.length > 0"
            class="mx-1 h-6 w-px bg-neutral-200"
          />

          <!-- New Order button -->
          <Link
            v-if="selectedCompanyId"
            :href="`/companies/${selectedCompanyId}/orders/new`"
            class="inline-flex items-center gap-1.5 rounded-lg bg-green-600 px-3 py-1.5 text-[13px] font-semibold text-white no-underline transition-colors hover:bg-green-700"
          >
            <i class="pi pi-plus text-[10px]" />
            New Order
          </Link>

          <div class="mx-1 h-6 w-px bg-neutral-200" />

          <!-- Profile dropdown -->
          <div ref="avatarDropdownRef" class="relative">
            <button
              class="flex cursor-pointer items-center gap-2 rounded-lg px-2 py-1.5 transition-colors hover:bg-neutral-100"
              @click="toggleAvatarDropdown"
            >
              <div
                class="from-navy-800 to-navy-600 flex h-7 w-7 items-center justify-center rounded-full bg-gradient-to-br text-[10px] font-bold text-white"
              >
                {{ userInitials }}
              </div>
              <span class="text-navy-800 text-[13px] font-medium">
                {{ userFirstName }}
              </span>
              <span class="text-[10px] text-neutral-400">&#9662;</span>
            </button>

            <!-- Profile dropdown menu -->
            <Transition
              enter-active-class="transition ease-out duration-100"
              enter-from-class="transform opacity-0 scale-95"
              enter-to-class="transform opacity-100 scale-100"
              leave-active-class="transition ease-in duration-75"
              leave-from-class="transform opacity-100 scale-100"
              leave-to-class="transform opacity-0 scale-95"
            >
              <div
                v-if="showAvatarDropdown"
                class="absolute right-0 mt-2 w-[220px] origin-top-right rounded-[10px] bg-white py-1 shadow-[0_8px_30px_rgba(0,0,0,0.1)]"
              >
                <!-- User info header -->
                <div class="border-b border-neutral-100 px-4 py-3">
                  <p class="text-navy-800 text-[13px] font-semibold">
                    {{ user?.name }}
                  </p>
                  <p class="text-[12px] text-neutral-400">
                    {{ user?.email }}
                  </p>
                </div>

                <!-- Menu items -->
                <div class="py-1">
                  <Link
                    v-if="canManageCompany && selectedCompanyId"
                    :href="`/companies/${selectedCompanyId}`"
                    class="text-navy-700 flex items-center px-4 py-2 text-[13px] no-underline transition-colors hover:bg-neutral-50"
                    @click="showAvatarDropdown = false"
                  >
                    Company Settings
                  </Link>
                  <Link
                    href="/account"
                    class="text-navy-700 flex items-center px-4 py-2 text-[13px] no-underline transition-colors hover:bg-neutral-50"
                    @click="showAvatarDropdown = false"
                  >
                    My Profile
                  </Link>
                </div>

                <div class="border-t border-neutral-100" />

                <div class="py-1">
                  <Link
                    href="/logout"
                    class="flex items-center px-4 py-2 text-[13px] text-red-600 no-underline transition-colors hover:bg-red-50"
                    @click="showAvatarDropdown = false"
                  >
                    Sign Out
                  </Link>
                </div>
              </div>
            </Transition>
          </div>
        </div>
      </div>
    </header>

    <!-- Main content -->
    <main v-if="user && !isLoginPage" class="bg-neutral-50">
      <slot />
    </main>

    <!-- Full-screen layout for login page or unauthenticated users -->
    <div v-if="!user || isLoginPage" class="min-h-screen flex-1">
      <slot />
    </div>
  </div>
</template>

<script setup lang="ts">
import { Head, Link, router, usePage } from "@inertiajs/vue3"
import ConfirmDialog from "primevue/confirmdialog"
import Toast from "primevue/toast"
import { useToast } from "primevue/usetoast"
import { computed, onMounted, onUnmounted, ref, watch } from "vue"

import { useCompanyState } from "../composables/useCompanyState"
import { apolloClient } from "../queries/client"
import LAYOUT_AGENTS from "../queries/layout/agents.gql"
import CURRENT_MEMBER from "../queries/member/current.gql"
import type { PageProps } from "../types/inertia"
import favicon from "/assets/favicon.ico"
import trellisLogo from "/assets/trellis-logo.svg"

const page = usePage<PageProps>()
const user = computed(() => page.props.current_user)
const toast = useToast()

const {
  selectedCompanyId,
  availableCompanies,
  setSelectedCompany,
  setAvailableCompanies,
} = useCompanyState()

const currentMemberRole = ref<string | null>(null)
const showAvatarDropdown = ref(false)
const showAgentDropdown = ref(false)
const showCompanySwitcher = ref(false)
const avatarDropdownRef = ref<HTMLElement | null>(null)
const agentDropdownRef = ref<HTMLElement | null>(null)
const companySwitcherRef = ref<HTMLElement | null>(null)

interface AgentRegistration {
  id: string
  databaseId: string
  name: string | null
  status: string
  lastHeartbeatAt: string | null
}

const agentRegistrations = ref<AgentRegistration[]>([])

const canManageCompany = computed(() => {
  return currentMemberRole.value?.toLowerCase() === "admin"
})

const selectedCompanyName = computed(() => {
  const company = availableCompanies.value.find(
    (c: { id: string; name: string }) => c.id === selectedCompanyId.value,
  )
  return company?.name || "Select Company"
})

const ordersPath = computed(() => {
  if (selectedCompanyId.value) {
    return `/companies/${selectedCompanyId.value}/orders`
  }
  return "/"
})

const userFirstName = computed(() => {
  return user.value?.name?.split(" ")[0] || ""
})

const userInitials = computed(() => {
  const name = user.value?.name || ""
  const parts = name.split(" ").filter(Boolean)
  if (parts.length >= 2) {
    return (
      parts[0].charAt(0) + parts[parts.length - 1].charAt(0)
    ).toUpperCase()
  }
  return name.charAt(0).toUpperCase()
})

const activeAgents = computed(() => {
  return agentRegistrations.value.filter((a) => a.status !== "revoked")
})

const allAgentsConnected = computed(() => {
  return (
    activeAgents.value.length > 0 &&
    activeAgents.value.every((a) => a.status === "connected")
  )
})

const formatHeartbeat = (isoString: string | null | undefined) => {
  if (!isoString) return "---"
  const date = new Date(isoString)
  const diffMs = Date.now() - date.getTime()
  const diffSecs = Math.floor(diffMs / 1000)
  if (diffSecs < 60) return `${diffSecs}s ago`
  const diffMins = Math.floor(diffSecs / 60)
  if (diffMins < 60) return `${diffMins}m ago`
  return date.toLocaleString()
}

const fetchCurrentMember = async (companyId: string) => {
  try {
    const result = await apolloClient.query({
      query: CURRENT_MEMBER,
      variables: { companyId },
      fetchPolicy: "network-only",
    })
    currentMemberRole.value = result.data?.currentMember?.role ?? null
  } catch (error) {
    console.error("Failed to fetch current member:", error)
    currentMemberRole.value = null
  }
}

const fetchAgentRegistrations = async (companyId: string) => {
  try {
    const result = await apolloClient.query({
      query: LAYOUT_AGENTS,
      variables: { companyId },
      fetchPolicy: "network-only",
    })
    agentRegistrations.value = result.data?.company?.agentRegistrations ?? []
  } catch (error) {
    console.error("Failed to fetch agent registrations:", error)
    agentRegistrations.value = []
  }
}

watch(selectedCompanyId, (newId) => {
  if (newId) {
    fetchCurrentMember(String(newId))
    fetchAgentRegistrations(String(newId))
  } else {
    currentMemberRole.value = null
    agentRegistrations.value = []
  }
})

const isLoginPage = computed(() => {
  return page.url.includes("/session/new") || page.url === "/session/new"
})

const getCompanyInitials = (name: string) => {
  return name
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase())
    .slice(0, 2)
    .join("")
}

const selectCompany = (companyId: string) => {
  setSelectedCompany(companyId)
  showCompanySwitcher.value = false
  router.visit(`/companies/${companyId}/orders`)
}

const toggleCompanySwitcher = () => {
  showCompanySwitcher.value = !showCompanySwitcher.value
  showAvatarDropdown.value = false
  showAgentDropdown.value = false
}

const toggleAvatarDropdown = () => {
  showAvatarDropdown.value = !showAvatarDropdown.value
  showAgentDropdown.value = false
  showCompanySwitcher.value = false
}

const toggleAgentDropdown = () => {
  showAgentDropdown.value = !showAgentDropdown.value
  showAvatarDropdown.value = false
  showCompanySwitcher.value = false
}

// Close dropdowns when clicking outside
const handleClickOutside = (event: MouseEvent) => {
  if (
    avatarDropdownRef.value &&
    !avatarDropdownRef.value.contains(event.target as Node)
  ) {
    showAvatarDropdown.value = false
  }
  if (
    agentDropdownRef.value &&
    !agentDropdownRef.value.contains(event.target as Node)
  ) {
    showAgentDropdown.value = false
  }
  if (
    companySwitcherRef.value &&
    !companySwitcherRef.value.contains(event.target as Node)
  ) {
    showCompanySwitcher.value = false
  }
}

onMounted(() => {
  router.on("finish", () => {
    const currentCompanyId = page.props.current_company_id
    if (currentCompanyId && currentCompanyId !== selectedCompanyId.value) {
      setSelectedCompany(currentCompanyId)
    }

    const flash = page.props.flash
    if (flash?.notice) {
      toast.add({
        severity: "success",
        summary: "Success",
        detail: flash.notice,
        life: 4000,
      })
    }
    if (flash?.alert) {
      toast.add({
        severity: "warn",
        summary: "Warning",
        detail: flash.alert,
        life: 4000,
      })
    }
    if (flash?.error) {
      toast.add({
        severity: "error",
        summary: "Error",
        detail: flash.error,
        life: 5000,
      })
    }
  })

  const companies = page.props.companies || []
  setAvailableCompanies(companies)

  if (page.props.current_company_id) {
    setSelectedCompany(page.props.current_company_id)
  }

  if (selectedCompanyId.value) {
    fetchCurrentMember(String(selectedCompanyId.value))
    fetchAgentRegistrations(String(selectedCompanyId.value))
  }

  document.addEventListener("click", handleClickOutside)
})

onUnmounted(() => {
  document.removeEventListener("click", handleClickOutside)
})
</script>
